import React, { useEffect, useState } from "react";
import { Download } from "lucide-react";
import InfoTip from "../../globalComponents/ui/InfoTip";
import {
  BRAND, DANGER, SUCCESS, GUIDE, GRID,
  Card, CardHeadRow, HeadLeft, CardTitle, HeadRight, Legend, Toggle, Select,
  TableWrap, Muted, HorizonSection, PillContainer, PillButton, DashboardPill, DashboardTable,
  DownloadBtn,
} from "./NetZeroDashboard.styles";
import {
  formatDashboardValue, calcDashboardCompletion, dashboardStatusLabel,
  prepareNearTermRows, prepareLongTermRows, prepareAnnualRows, downloadDashboardExcel,
} from "./netzero.utils";
import { PathwayChart } from "../ChartUtility/recharts/PathwayChart";
import { RBarChart } from "../ChartUtility/recharts";


function HorizonDashboardContainer({ scope1Data, scope2Data, scope3Data, loading }) {
  const [horizonTab, setHorizonTab] = useState("near-term");
  const [annualYear, setAnnualYear] = useState(null);
  const [view, setView] = useState("table");
  const [included, setIncluded] = useState({ scope1: true, scope2: true, scope3: true });
  const [showIndividual, setShowIndividual] = useState(false);
  const [showS3Categories, setShowS3Categories] = useState(false);

  useEffect(() => {
    const src = scope3Data?.pathway || scope1Data?.rows;
    if (!src) return;
    const currentYear = new Date().getFullYear();
    const years = src.map((r) => r.year);
    if (years.includes(currentYear)) {
      setAnnualYear(currentYear);
    } else if (years.length) {
      setAnnualYear(years[years.length - 1]);
    }
  }, [scope3Data, scope1Data]);

  if (loading) return <Muted>Loading Dashboards…</Muted>;
  if (!scope1Data || !scope2Data) return null;

  const scopeMeta = {
    scope1: { label: "Scope 1", color: BRAND, rows: scope1Data.rows },
    scope2: { label: "Scope 2", color: "#a0651a", rows: scope2Data.rows },
    scope3: { label: "Scope 3", color: SUCCESS, rows: scope3Data?.pathway || [] },
  };
  const scopeAvailable = { scope1: !!scope1Data, scope2: !!scope2Data, scope3: !!scope3Data };
  const availableScopeKeys = ["scope1", "scope2", "scope3"].filter((k) => scopeAvailable[k]);
  const selectedKeys = availableScopeKeys.filter((k) => included[k]);
  const rowScope = (kpi) =>
    kpi.startsWith("Scope 1") ? "scope1" : kpi.startsWith("Scope 2") ? "scope2" : kpi.startsWith("Grand Total") ? "grand" : "scope3";
  const GRAND_COMPONENTS = new Set(["Scope 1 (tCO₂e)", "Scope 2 (tCO₂e)", "Scope 3 Total (tCO₂e)", "Total Scope 3 (tCO₂e)"]);
  const displayRows = (rows) => {
    const kept = rows.filter((r) => {
      const s = rowScope(r.kpi);
      return s !== "grand" && included[s];
    });
    const comps = kept.filter((r) => GRAND_COMPONENTS.has(r.kpi));
    if (comps.length <= 1) return kept;
    const sum = (f) => comps.reduce((acc, r) => acc + (Number(r[f]) || 0), 0);
    const hasTraj = comps[0].trajectory !== undefined;
    const actField = hasTraj ? "trajectory" : "actualValue";
    const actVal = comps.every((r) => r[actField] != null) ? sum(actField) : null;
    const baseValue = sum("baseValue");
    const targetValue = sum("targetValue");
    const gStatus = () => {
      if (actVal == null) return "NO_DATA";
      if (targetValue != null && actVal <= targetValue) return "ACHIEVED";
      const { baseYear, targetYear, actualYear } = comps[0];
      if (baseYear != null && targetYear != null && actualYear != null && targetYear > baseYear && baseValue > targetValue) {
        const progress = (baseValue - actVal) / (baseValue - targetValue);
        const elapsed = (actualYear - baseYear) / (targetYear - baseYear);
        return progress >= elapsed ? "ON_TRACK" : "OFF_TRACK";
      }
      return "OFF_TRACK";
    };
    const grand = {
      kpi: "Grand Total S1+S2+S3 (tCO₂e)",
      baseYear: comps[0].baseYear,
      baseValue,
      targetYear: comps[0].targetYear,
      targetValue,
      [actField]: actVal,
      status: gStatus(),
      type: "emission",
    };
    if (hasTraj) { grand.minReduction = "90%"; grand.residual = "All scopes"; }
    else { grand.actualYear = comps[0].actualYear; grand.minAmbition = "—"; }
    return [...kept, grand];
  };

  const ntRows = prepareNearTermRows(scope1Data, scope2Data, scope3Data);
  const ltRows = prepareLongTermRows(scope1Data, scope2Data, scope3Data);
  const annualRows = prepareAnnualRows(scope1Data, scope2Data, scope3Data, annualYear);

  const getActiveRows = () => {
    if (horizonTab === "near-term") return ntRows;
    if (horizonTab === "long-term") return ltRows;
    return annualRows;
  };

  const activeRows = getActiveRows();
  const titleText =
    horizonTab === "near-term" ? "Near-term dashboard" :
    horizonTab === "long-term" ? "Long-term dashboard" :
    "Annual progress dashboard";

  const exportLabel = 
    horizonTab === "near-term" ? "Near_Term_Dashboard" :
    horizonTab === "long-term" ? "Long_Term_Pathway_Dashboard" :
    `Annual_Progress_Tracker_${annualYear}`;

  const fmt = (r, f) => formatDashboardValue(r[f], r.type);
  const statusCol = {
    header: "Status",
    cell: (r) => <DashboardPill $status={r.status}>{dashboardStatusLabel[r.status]}</DashboardPill>,
  };
  const TABLE_CONFIG = {
    "near-term": {
      rows: ntRows,
      columns: [
        { header: "Scope / KPI", tip: "Emission scopes and categories covered by the targets.", left: true, cell: (r) => r.kpi },
        { header: "Base Year", tip: "The base year chosen for setting the targets.", cell: (r) => r.baseYear },
        { header: "Base Value", tip: "Absolute emissions in the base year (tCO₂e).", cell: (r) => fmt(r, "baseValue") },
        { header: "NT Target", tip: "Near-term allowed target emissions in target year (4.2%/yr linear reduction for Scope 1 & 2, 2.5%/yr for Scope 3).", cell: (r) => fmt(r, "targetValue") },
        { header: "Most Recent Actual", tip: "Consolidated emissions of the most recent year with actual data across scopes (tCO₂e).", cell: (r) => fmt(r, "actualValue") },
        { header: "% Toward Target", tip: "Progress achieved toward the near-term target: (Base − Actual) ÷ (Base − Target). Values over 100% mean target is already met; negative values mean emissions increased.", cell: (r) => calcDashboardCompletion(r) },
        { ...statusCol, tip: "Achieved if target is met, On Track if emissions are below the linear pathway, and Off Track if they exceed it." },
      ],
    },
    "long-term": {
      rows: ltRows,
      columns: [
        { header: "Scope / KPI", tip: "Emission scopes and categories covered.", left: true, cell: (r) => r.kpi },
        { header: "Base Year", tip: "The base year of the target config.", cell: (r) => r.baseYear ?? "—" },
        { header: "Base Value (tCO₂e)", tip: "Emissions of each scope in the base year (tCO₂e).", cell: (r) => fmt(r, "baseValue") },
        { header: "LT Target 2050 (tCO₂e)", tip: "The long-term net-zero target value (requires a flat 90% reduction from the base year emissions).", cell: (r) => fmt(r, "targetValue") },
        { header: "Min Reduction Required", tip: "The minimum reduction percentage required by SBTi for long-term target (flat 90% contraction across all scopes by 2050).", cell: (r) => r.minReduction },
        { header: "Current Trajectory", tip: "Emissions of the most recent year with actual data, representing the current trajectory toward net-zero.", cell: (r) => fmt(r, "trajectory") },
        { header: "Residual to Neutralise", tip: "Residual emissions that cannot be reduced further and must be neutralized using permanent carbon removals (maximum of 10% of base emissions).", cell: (r) => r.residual },
        { ...statusCol, tip: "On Track if the current trajectory is below the required pathway ceiling, Action Needed/Off Track if it is above." },
      ],
    },
    "annual": {
      rows: annualRows,
      columns: [
        { header: "KPI", tip: "Emission scopes and categories covered.", left: true, cell: (r) => r.kpi },
        { header: "Base Year Value", tip: "The emissions of the base year (tCO₂e).", cell: (r) => fmt(r, "baseValue") },
        { header: "Target Value", tip: "The allowed target ceiling for the selected year, calculated from the linear target pathway.", cell: (r) => fmt(r, "targetValue") },
        { header: <>{annualYear} Actual</>, tip: "The actual emissions recorded in the selected year (tCO₂e).", cell: (r) => fmt(r, "actualValue") },
        { header: "% Target Completion", tip: "How much of the required target reduction for that specific year has been achieved: (Base − Actual) ÷ (Base − Target). 100% means the target was fully achieved.", cell: (r) => calcDashboardCompletion(r) },
        { ...statusCol, tip: "Achieved if emissions are below the target value for that year, Off Track if they exceed it." },
      ],
    },
  };

  const renderDashboardTable = () => {
    const { rows, columns } = TABLE_CONFIG[horizonTab] ?? TABLE_CONFIG.annual;
    return (
      <TableWrap>
        <DashboardTable>
          <thead>
            <tr>
              {columns.map((col, i) => (
                <th key={i} style={col.left ? { textAlign: "left" } : undefined}>{col.header}<InfoTip text={col.tip} /></th>
              ))}
            </tr>
          </thead>
          <tbody>
            {displayRows(rows).map((r, idx) => (
              <tr key={idx}>
                {columns.map((col, i) => (
                  <td key={i} style={col.left ? { textAlign: "left" } : undefined}>{col.cell(r)}</td>
                ))}
              </tr>
            ))}
          </tbody>
        </DashboardTable>
      </TableWrap>
    );
  };

  const buildCombinedSeries = () => {
    if (!selectedKeys.length) return { series: [] };
    const maps = Object.fromEntries(selectedKeys.map((k) => [k, Object.fromEntries(scopeMeta[k].rows.map((r) => [r.year, r]))]));
    const baseOf = Object.fromEntries(selectedKeys.map((k) => [k, scopeMeta[k].rows[0]?.year ?? 0]));
    const yearSet = new Set();
    selectedKeys.forEach((k) => scopeMeta[k].rows.forEach((r) => yearSet.add(r.year)));
    const years = [...yearSet].sort((a, b) => a - b);
    let lastActual = years[0];
    years.forEach((y) => {
      const started = selectedKeys.filter((k) => y >= baseOf[k]);
      if (started.length && started.every((k) => maps[k][y]?.actual != null)) lastActual = y;
    });

    let lastActualVal = 0;
    selectedKeys.forEach((k) => {
      lastActualVal += maps[k][lastActual]?.actual ?? 0;
    });

    const targetYear = horizonTab === "near-term" ? scope1Data.nearTermTargetYear : scope1Data.longTermTargetYear;
    
    let targetVal = 0;
    selectedKeys.forEach((k) => {
      const rowAtTarget = scopeMeta[k].rows.find(r => r.year === targetYear);
      targetVal += rowAtTarget?.required ?? 0;
    });

    let onTrackRate = 0;
    if (targetYear > lastActual && lastActualVal > targetVal) {
      onTrackRate = 1 - Math.pow(Math.max(targetVal, 1) / lastActualVal, 1 / (targetYear - lastActual));
    }

    const series = years.map((y) => {
      const started = selectedKeys.filter((k) => y >= baseOf[k]);
      const point = { year: y };
      let required = 0, actSum = 0, actAll = started.length > 0;
      started.forEach((k) => {
        required += maps[k][y]?.required ?? 0;
        const a = maps[k][y]?.actual;
        point[`${k}_actual`] = a ?? null;
        if (a != null) actSum += a; else actAll = false;
      });
      const actual = actAll ? actSum : null;
      point.required = required;
      point.actual = y <= lastActual ? actual : null;
      point.onTrack = (y >= lastActual && y <= targetYear && lastActualVal > 0)
        ? lastActualVal * Math.pow(1 - onTrackRate, y - lastActual)
        : null;
      return point;
    });
    return { series };
  };

  const renderDashboardCharts = () => {
    const { series } = buildCombinedSeries();
    const ntYear = scope1Data.nearTermTargetYear;
    const nzYear = scope1Data.longTermTargetYear;

    const lastPt = [...series].reverse().find((r) => r.actual != null);
    const floor = series.length ? series[series.length - 1].required : null;
    let banner = null;
    if (horizonTab === "annual") {
      banner = "Each year's actual is compared with the required emissions for that year — shaded red areas show over-budget emissions, green areas show savings.";
    } else if (lastPt && floor != null && nzYear > lastPt.year) {
      const ratePct = lastPt.actual > floor ? (1 - Math.pow(Math.max(floor, 1) / lastPt.actual, 1 / (nzYear - lastPt.year))) * 100 : 0;
      banner = <>From your {lastPt.year} actual of <b>{Number(lastPt.actual).toLocaleString()}</b> tCO₂e, you need about <b style={{ color: GUIDE }}>{ratePct.toFixed(1)}%/yr</b> to reach net-zero by {nzYear}.</>;
    }
    const bannerEl = banner && (
      <div style={{ background: "#f5f3fb", border: "1px solid #e4def0", borderRadius: 8, margin: "0 20px 12px", padding: "10px 14px", fontSize: 13, color: "#4a4368" }}>{banner}</div>
    );

    if (horizonTab === "annual") {
      const annualData = series.filter((r) => r.actual != null);
      return (
        <>
          {bannerEl}
          <RBarChart
            data={annualData}
            xKey="year"
            height={360}
            unit="tCO₂e"
            series={[
              { key: "required", name: "Required", color: BRAND, maxBarSize: 34 },
              { key: "actual", name: "Actual", maxBarSize: 34, getColor: (r) => (r.actual <= r.required ? SUCCESS : DANGER) },
            ]}
          />
          <Legend style={{ justifyContent: "center", marginTop: 8 }}>
            <span><span style={{ width: 12, height: 8, background: BRAND, display: "inline-block", borderRadius: 2 }} />Required</span>
            <span><span style={{ width: 12, height: 8, background: SUCCESS, display: "inline-block", borderRadius: 2 }} />Actual — on track</span>
            <span><span style={{ width: 12, height: 8, background: DANGER, display: "inline-block", borderRadius: 2 }} />Actual — off track</span>
          </Legend>
        </>
      );
    }

    const data = horizonTab === "near-term" ? series.filter((r) => r.year <= ntYear + 1) : series;
    const overlays = showIndividual
      ? selectedKeys.map((k) => ({ key: `${k}_actual`, name: scopeMeta[k].label, color: scopeMeta[k].color }))
      : [];

    return (
      <>
        {bannerEl}
        <PathwayChart
          rows={data}
          nearTermYear={ntYear}
          netZeroYear={horizonTab === "long-term" ? nzYear : null}
          overlays={overlays}
          height={360}
          xInterval={horizonTab === "near-term" ? 0 : 4}
        />
        <Legend style={{ justifyContent: "center", marginTop: 8, flexWrap: "wrap" }}>
          <span><span style={{ width: 14, height: 2, background: BRAND, display: "inline-block" }} />Required (to date)</span>
          <span><span style={{ width: 14, height: 0, borderTop: `2px dashed ${GUIDE}`, display: "inline-block" }} />On Track Path (projected)</span>
          <span><span style={{ width: 8, height: 8, borderRadius: "50%", background: DANGER, display: "inline-block" }} />Actual</span>
          {showIndividual && selectedKeys.map((k) => (
            <span key={k}><span style={{ width: 14, height: 2, background: scopeMeta[k].color, display: "inline-block" }} />{scopeMeta[k].label}</span>
          ))}
        </Legend>
      </>
    );
  };

  const getAvailableYears = () => {
    const years = new Set();
    (selectedKeys.length ? selectedKeys : availableScopeKeys).forEach((k) => scopeMeta[k].rows.forEach((r) => years.add(r.year)));
    return [...years].sort((a, b) => b - a);
  };

  const renderS3Breakdown = () => {
    if (!scope3Data) return null;
    const useLT = horizonTab === "long-term";
    const inTarget = (cat) => (useLT ? cat.includeLt : cat.includeNt);
    const targetOf = (cat) => (useLT ? cat.longTermTarget : cat.nearTermTarget);
    const recentOf = (cat) => {
      const ys = Object.keys(cat.actualsByYear || {}).map(Number);
      return ys.length ? cat.actualsByYear[Math.max(...ys)] : null;
    };
    const recentActuals = (scope3Data.pathway || []).filter((r) => r.actual != null);
    const recentYear = recentActuals.length ? recentActuals[recentActuals.length - 1].year : scope3Data.baseYear;
    const cats = (scope3Data.categories || [])
      .filter((cat) => cat.baseEmissions > 0 && inTarget(cat))
      .map((cat) => ({ name: cat.name, base: cat.baseEmissions, recent: recentOf(cat), target: targetOf(cat) }))
      .sort((a, b) => b.base - a.base);
    if (!cats.length) return null;
    const targetLabel = useLT ? "LT target" : "NT target";
    return (
      <div style={{ borderTop: `1px solid ${GRID}`, padding: "16px 20px" }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: "#1e293b", marginBottom: 2 }}>
          Scope 3 category breakdown (base year {scope3Data.baseYear})
        </div>
        <div style={{ fontSize: 12, color: "#64748b", marginBottom: 12 }}>
          Base year, {recentYear} actual, and {targetLabel} per category in the {useLT ? "long-term" : "near-term"} target — the gap between actual and target is what's still to cut.
        </div>
        <RBarChart
          data={cats}
          xKey="name"
          height={260}
          unit="tCO₂e"
          angledLabels
          series={[
            { key: "base", name: "Base year", color: "#94a3b8" },
            { key: "recent", name: `${recentYear} actual`, color: DANGER },
            { key: "target", name: targetLabel, color: SUCCESS },
          ]}
        />
        <Legend style={{ justifyContent: "center", marginTop: 8 }}>
          <span><span style={{ width: 12, height: 8, background: "#94a3b8", display: "inline-block", borderRadius: 2 }} />Base year</span>
          <span><span style={{ width: 12, height: 8, background: DANGER, display: "inline-block", borderRadius: 2 }} />{recentYear} actual</span>
          <span><span style={{ width: 12, height: 8, background: SUCCESS, display: "inline-block", borderRadius: 2 }} />{targetLabel}</span>
        </Legend>
      </div>
    );
  };

  return (
    <HorizonSection>
      <PillContainer>
        <PillButton $active={horizonTab === "near-term"} onClick={() => setHorizonTab("near-term")}>Near-term</PillButton>
        <PillButton $active={horizonTab === "long-term"} onClick={() => setHorizonTab("long-term")}>Long-term</PillButton>
        <PillButton $active={horizonTab === "annual"} onClick={() => setHorizonTab("annual")}>Annual</PillButton>
      </PillContainer>

      <Card>
        <CardHeadRow>
          <HeadLeft>
            <CardTitle>{titleText}</CardTitle>
            {horizonTab === "annual" && view === "table" && (
              <Select
                value={annualYear || ""}
                onChange={(e) => setAnnualYear(Number(e.target.value))}
                style={{ marginLeft: 8 }}
              >
                {getAvailableYears().map(y => (
                  <option key={y} value={y}>{y}</option>
                ))}
              </Select>
            )}
            <DownloadBtn title="Download Excel" onClick={() => downloadDashboardExcel(displayRows(activeRows), exportLabel)}>
              <Download size={12} /> Download Excel
            </DownloadBtn>
          </HeadLeft>
          <HeadRight>
            <Toggle>
              <button type="button" className={view === "chart" ? "active" : ""} onClick={() => setView("chart")}>Chart</button>
              <button type="button" className={view === "table" ? "active" : ""} onClick={() => setView("table")}>Table</button>
            </Toggle>
          </HeadRight>
        </CardHeadRow>

        <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 10, padding: "10px 20px", borderBottom: `1px solid #eef2f6` }}>
          <span style={{ fontSize: 11, fontWeight: 700, color: "#94a3b8", textTransform: "uppercase", letterSpacing: "0.04em" }}>Include</span>
          {availableScopeKeys.map((k) => (
            <label key={k} style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 12.5, fontWeight: 600, color: "#334155", cursor: "pointer", padding: "5px 11px", border: `1px solid ${included[k] ? scopeMeta[k].color : "#e2e8f0"}`, borderRadius: 999, background: included[k] ? `${scopeMeta[k].color}14` : "#fff" }}>
              <input type="checkbox" checked={included[k]} onChange={() => setIncluded((p) => ({ ...p, [k]: !p[k] }))} style={{ accentColor: scopeMeta[k].color }} />
              <span style={{ width: 9, height: 9, borderRadius: "50%", background: scopeMeta[k].color, display: "inline-block" }} />
              {scopeMeta[k].label}
            </label>
          ))}
          <span style={{ flex: 1 }} />
          {view === "chart" && horizonTab !== "annual" && (
            <label style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 12, color: "#64748b", cursor: "pointer" }}>
              <input type="checkbox" checked={showIndividual} onChange={() => setShowIndividual((v) => !v)} style={{ accentColor: BRAND }} />
              Overlay each scope's actual line
            </label>
          )}
          {view === "chart" && horizonTab !== "annual" && scopeAvailable.scope3 && included.scope3 && (
            <label style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 12, color: "#64748b", cursor: "pointer" }}>
              <input type="checkbox" checked={showS3Categories} onChange={() => setShowS3Categories((v) => !v)} style={{ accentColor: BRAND }} />
              Show Scope 3 category breakdown
            </label>
          )}
        </div>

        {selectedKeys.length === 0 ? (
          <Muted>Select at least one scope to include.</Muted>
        ) : (
          <>
            {view === "table" ? renderDashboardTable() : renderDashboardCharts()}
            {view === "chart" && horizonTab !== "annual" && showS3Categories && scopeAvailable.scope3 && included.scope3 && renderS3Breakdown()}
          </>
        )}
      </Card>
    </HorizonSection>
  );
}

export default HorizonDashboardContainer;
